import { BaseService } from "@/services/BaseService";
import logger from "@/utils/logger";

// Types matching your backend API responses
export interface UserPermissionResponse {
    userId: number;
    permissions: string[];
    count: number;
    loadedAt: string;
}

export interface PermissionCheckResponse {
    userId: number;
    permissionCode: string;
    hasPermission: boolean;
    checkedAt: string;
}

export interface UIFeaturePermissions {
    userId: number;
    features: {
        [featureName: string]:
            | {
                  canView?: boolean;
                  canCreate?: boolean;
                  canEdit?: boolean;
                  canDelete?: boolean;
                  canTrack?: boolean;
                  availablePermissions?: Array<{
                      permissionCode: string;
                      permissionName: string;
                  }>;
              }
            | {
                  [permissionCode: string]: {
                      hasPermission: boolean;
                      permissionName: string;
                      permissionDescription: string;
                  };
              };
    };
    rawPermissions: string[];
    loadedAt: string;
    cacheExpiry: string;
}

export interface PermissionViewModel {
    permissionId: number;
    permissionCode: string;
    permissionName: string;
    permissionDescription: string;
}

// 🔥 FIX: Cache for permission API calls
const uiFeaturesCache: Map<
    number,
    { data: UIFeaturePermissions; timestamp: number }
> = new Map();
const userPermissionsCache: Map<
    number,
    { data: UserPermissionResponse; timestamp: number }
> = new Map();
const PERMISSION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// 🔥 FIX: Pending request deduplication
const pendingUIFeaturesRequest: Map<
    number,
    Promise<UIFeaturePermissions>
> = new Map();

class PermissionServiceClass extends BaseService {
    protected get baseUrl(): string {
        return `${process.env.NEXT_PUBLIC_API_URL}/api/Permission`;
    }

    /**
     * Get all permissions for a user
     */
    async getUserPermissions(userId: number): Promise<UserPermissionResponse> {
        // 🔥 FIX: Check cache first
        const cached = userPermissionsCache.get(userId);
        if (
            cached &&
            Date.now() - cached.timestamp < PERMISSION_CACHE_DURATION
        ) {
            logger.info("✅ Using cached user permissions for:", userId);
            return cached.data;
        }

        const result = await this.get<UserPermissionResponse>(`user/${userId}`);

        // Cache the result
        userPermissionsCache.set(userId, {
            data: result,
            timestamp: Date.now(),
        });

        return result;
    }

    /**
     * Check if user has a specific permission
     */
    async checkUserPermission(
        userId: number,
        permissionCode: string
    ): Promise<PermissionCheckResponse> {
        return this.get(`user/${userId}/check/${permissionCode}`);
    }

    /**
     * Get organized UI feature permissions for a user
     */
    async getUserUIFeatures(userId: number): Promise<UIFeaturePermissions> {
        // 🔥 FIX: Check cache first
        const cached = uiFeaturesCache.get(userId);
        if (
            cached &&
            Date.now() - cached.timestamp < PERMISSION_CACHE_DURATION
        ) {
            logger.info("✅ Using cached UI features for:", userId);
            return cached.data;
        }

        // 🔥 FIX: Check if request is already in progress
        const pending = pendingUIFeaturesRequest.get(userId);
        if (pending) {
            logger.info("🔄 Reusing pending UI features request for:", userId);
            return pending;
        }

        // Create new request
        const request = (async () => {
            try {
                const result = await this.get<UIFeaturePermissions>(
                    `user/${userId}/ui-features`
                );

                // Cache the result
                uiFeaturesCache.set(userId, {
                    data: result,
                    timestamp: Date.now(),
                });

                return result;
            } finally {
                pendingUIFeaturesRequest.delete(userId);
            }
        })();

        pendingUIFeaturesRequest.set(userId, request);
        return request;
    }

    /**
     * Check if user has any of the specified permissions
     */
    async userHasAnyPermission(
        userId: number,
        permissionCodes: string[]
    ): Promise<boolean> {
        try {
            const userPermissions = await this.getUserPermissions(userId);
            return permissionCodes.some((code) =>
                userPermissions.permissions.includes(code)
            );
        } catch (error) {
            console.error("Error checking user permissions:", error);
            return false;
        }
    }

    /**
     * Check if user has all of the specified permissions
     */
    async userHasAllPermissions(
        userId: number,
        permissionCodes: string[]
    ): Promise<boolean> {
        try {
            const userPermissions = await this.getUserPermissions(userId);
            return permissionCodes.every((code) =>
                userPermissions.permissions.includes(code)
            );
        } catch (error) {
            console.error("Error checking user permissions:", error);
            return false;
        }
    }
}

// Export singleton instance
export const PermissionService = new PermissionServiceClass();
